import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "src/context/AuthContext";
import { useSubscriptionWorkflowStore } from "src/store/subscriptionWorkflowStore";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Card, CardContent } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { RadioGroup, RadioGroupItem } from "src/components/ui/RadioGroup";
import { Label } from "src/components/ui/Label";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from "src/components/ui/Breadcrumb";
import {
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Home
} from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "src/routes/RouteConfig";
import { useToast } from "src/hooks/useToast";

const NewSubscriptionSelect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    currentWorkflow,
    availableProducts,
    selectedProduct,
    isLoadingProducts,
    fetchAvailableProducts,
    selectProduct,
    createWorkflow
  } = useSubscriptionWorkflowStore();
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  useEffect(() => {
    // Fetch products on component mount
    fetchAvailableProducts();
  }, [fetchAvailableProducts]);

  // Get product type from location state or current workflow
  const productType = location.state?.productType || currentWorkflow?.product_type;

  // Filter products by workflow type
  const getFilteredProducts = () => {
    if (!productType) return [];
    return availableProducts.filter(product => product.type === productType);
  };

  const filteredProducts = getFilteredProducts();

  // Handle product selection
  const handleProductSelect = async (product: any) => {
    if (!user?.id) return;
    selectProduct(product);
  };

  // Handle step completion
  const handleContinue = async () => {
    if (!selectedProduct || !user?.id) {
      toast({
        title: "Selection Required",
        description: "Please select a product to continue",
      });
      return;
    }

    const workflowId = await createWorkflow(user.id, selectedProduct.type as any, selectedProduct.id);

    if (workflowId) {
      toast({
        title: "Subscription Started",
        description: "Please complete this subscription in one session.",
      });

      // Move to next step based on product type
      if (selectedProduct.type === 'booster') {
        // For booster, go directly to pricing (step 3)
        navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION_PRICING.path);
      } else {
        // For custom/preparation, go to curriculum configuration (step 2)
        navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION_CONFIGURE.path);
      }
    } else {
      toast({
        title: "Error",
        description: "Failed to create workflow with selected product",
      });
    }
  };

  // Get product features
  const getProductFeatures = (product: any) => {
    if (!product.features) return [];
    return Object.entries(product.features).map(([key, value]) => ({
      key,
      value,
      label: key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    }));
  };

  const breadcrumbItems = [
    { label: "Dashboard", href: ROUTES.STUDENT_DASHBOARD.path },
    { label: "Subscriptions", href: ROUTES.STUDENT_SUBSCRIPTIONS.path },
    { label: "New Subscription", href: ROUTES.STUDENT_NEW_SUBSCRIPTION.path },
    { label: "Select Package", href: null }
  ];

  useEffect(() => {
    setTitle("Select Package");
    setDescription("Choose the package that best fits your learning needs");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  if (!productType) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 mb-4">No product type specified.</p>
        <Button onClick={() => navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION.path)}>
          Start New Subscription
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_DASHBOARD.path} className="flex items-center">
                  <Home className="h-4 w-4 mr-1" />
                  Dashboard
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_SUBSCRIPTIONS.path}>
                  Subscriptions
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_NEW_SUBSCRIPTION.path}>
                  New Subscription
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Select Package</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="space-y-6">
        <Card>
          <CardContent>
            {/* Product Selection */}
            {isLoadingProducts ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No products available for this package type.</p>
              </div>
            ) : (
              <RadioGroup
                value={selectedProduct?.id || ''}
                onValueChange={(value) => {
                  const product = filteredProducts.find(p => p.id === value);
                  if (product) handleProductSelect(product);
                }}
              >
                <div className="grid grid-cols-1 gap-4">
                  {filteredProducts.map((product) => {
                    const features = getProductFeatures(product);
                    const isSelected = selectedProduct?.id === product.id;

                    return (
                      <div key={product.id} className="relative">
                        <Label
                          htmlFor={product.id}
                          className={`
                            block rounded-lg border-2 p-6 transition-all
                            ${isSelected
                              ? 'border-blue-500 bg-blue-50 cursor-pointer'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                            }
                          `}
                        >
                          <div className="flex items-start space-x-4">
                            <RadioGroupItem
                              value={product.id}
                              id={product.id}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="text-lg font-semibold">
                                  {product.name}
                                </h3>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-blue-600">
                                    ${product.price.toFixed(2)}
                                  </p>
                                  <p className="text-sm text-gray-500">{product.duration_days} days</p>
                                </div>
                              </div>
                              <p className="mb-4 text-gray-600">
                                {product.description}
                              </p>

                              {features.length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                  {features.map((feature, index) => (
                                    <div key={index} className="flex items-center text-sm">
                                      <CheckCircle className="h-3 w-3 mr-2 text-green-500" />
                                      <span>{feature.label}: {String(feature.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            )}
          </CardContent>
        </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION.path)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedProduct}
        >
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  </>
);
};

export default NewSubscriptionSelect;
